import { reportMessage } from "utils/sentry/reportError";
import { sanitizeRoute } from "utils/privacy/telemetryUrl";

// Why the media died, which decides whether the event is ACTIONABLE.
//
//  "load"      — the url could have been an image and the browser still could not load it.
//                Retrying was reasonable. Cause is usually outside our code: a deleted object,
//                a blocked request, a flaky network. This is the historical default.
//  "non_image" — the url is structurally incapable of being an image (an archive, a document).
//                Always our bug: something handed a non-image url to an image element.
//  "invalid_src" — the src was never a url: blank, whitespace, or a stringified JSON payload.
//                Also always our bug, and worth its own class because the extras CANNOT tell the
//                two apart — a blank src resolves against the document, so it reports as
//                `app.refabric.com` + `/` no matter which call site produced it (Sentry
//                7692940189, six weeks of events across three unrelated routes in one issue).
//
// Sentry 7656627839 is why this exists. It fingerprinted on component + hostname only, so a ZIP
// rendered in an <img> (our bug, fixable) shared ONE issue with valid images failing on one
// user's network (not our bug, not fixable) — and the fixable half was invisible inside the noise.
export type MediaFailureReason = "load" | "non_image" | "invalid_src";

export interface MediaLoadFailureOptions {
  // Component name, e.g. "RetryImg" — part of the dedup key so each shared
  // component surfaces its own signal.
  component: string;
  kind: "img" | "video";
  src: string;
  retries?: number;
  reason?: MediaFailureReason;
}

// Once-per-session guard (same shape as canvasMapper's mis-save reporter,
// REF-5272): module-scope Set, add-before-capture. Keyed by component + URL
// origin + truncated path with query/hash stripped — a raw URL key would be
// unbounded (signed URLs differ per view) and would leak signature params into
// the key. MAX_REPORTS caps the session as a whole so a page of 200 dead tiles
// can't flood Sentry (the REFABRIC-FRONTEND-3R noise lesson).
const MAX_REPORTS_PER_SESSION = 50;
const PATH_PREFIX_LENGTH = 80;
const reportedMediaKeys = new Set<string>();

const dedupKey = (component: string, src: string): string => {
  try {
    const url = new URL(src, window.location.origin);

    return `${component}:${url.hostname}:${url.pathname.slice(0, PATH_PREFIX_LENGTH)}`;
  } catch {
    return `${component}:${src.split("?")[0].split("#")[0].slice(0, PATH_PREFIX_LENGTH)}`;
  }
};

/**
 * Report a TERMINAL media load failure (UI Regression Defense Layer 4, REF-6063).
 *
 * Call only when recovery is exhausted (RetryImg budget spent, LazyImg/native
 * onError fired) — not per retry attempt. Safe everywhere: `reportMessage`
 * no-ops when the Sentry client is absent (it is host-gated to production), so
 * correctness is pinned by Jest, not by watching dev Sentry.
 *
 * Extras stay primitive and never include the full URL — signed GCS query
 * strings are credentials.
 */
export const reportMediaLoadFailure = ({ component, kind, src, retries, reason = "load" }: MediaLoadFailureOptions): void => {
  if (reportedMediaKeys.size >= MAX_REPORTS_PER_SESSION) return;

  // Dedup stays component + origin + path, deliberately WITHOUT `reason`. Adding it was tempting
  // ("a page could hit both classes") but `reason` is a pure function of `src`, so for one path it
  // is constant — the only way two reasons share a key is the 80-char path truncation colliding two
  // different urls, which is not worth relaxing a stated once-per-session contract for.
  const key = dedupKey(component, src);
  if (reportedMediaKeys.has(key)) return;
  reportedMediaKeys.add(key);

  let hostname = "";
  let pathPrefix = "";
  try {
    const url = new URL(src, window.location.origin);
    hostname = url.hostname;
    // Sanitise BEFORE truncating: a cut path can no longer be recognised as a token route (REF-6708).
    pathPrefix = sanitizeRoute(url.pathname).slice(0, PATH_PREFIX_LENGTH);
  } catch {
    pathPrefix = sanitizeRoute(src.split("?")[0].split("#")[0]).slice(0, PATH_PREFIX_LENGTH);
  }

  reportMessage(`Media failed to load terminally: ${component} (${kind})`, {
    source: "render",
    featureArea: "media",
    level: "warning",
    // The "load" fingerprint is byte-identical to the pre-split one ON PURPOSE — that keeps issue
    // 7656627839's history attached to the class it will keep receiving, instead of orphaning a
    // month of events into a dead issue. Only the new, actionable class earns a new grouping.
    fingerprint: reason === "load" ? ["media-load-failure", component, hostname] : ["media-load-failure", component, hostname, reason],
    extras: {
      component,
      kind,
      hostname,
      path_prefix: pathPrefix,
      reason,
      ...(retries !== undefined && { retries }),
    },
  });
};
